import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AssetCategory,
    ChartAccount,
    FixedAsset,
    JournalEntry,
    LocaleCode,
} from '@/api/models';
import type {
    CreateFixedAssetRequest,
    UpdateFixedAssetRequest,
} from '@/api/handlers/resources/fixedAssets/requests';
import {
    useCreateFixedAssetMutation,
    useInvalidatePostedAssetEntry,
    useUpdateFixedAssetMutation,
} from '@/api/queries';
import { parseGelToTetri } from '@/pages/accounting/books/chartOfAccounts/utils';
import { newEntryId } from '@/pages/accounting/journalEntry/utils';
import { resolveLabel } from '@/pages/accounting/statements/statementMeta';
import { fixedAssetErrorMessage, periodFromDate } from '../../fixedAssetsMeta';
import type { AccountOption } from '@/pages/accounting/journalEntry/journalEntryMeta';

type UseAssetFormParams = {
    /** The asset being edited; `null` = create. */
    asset: FixedAsset | null;
    categories: AssetCategory[];
    /** The chart — the funding-account picker and the mapping display read it. */
    accounts: ChartAccount[];
    locale: LocaleCode;
    /** Close the host drawer. */
    onClose: () => void;
    /** Fired once, after a real create success — never on edit, never on cancel. */
    onSaved?: () => void;
};

/** The three accounts a category maps to, resolved for read-only display. */
export type MappedAccount = {
    labelKey: string;
    code: string;
    name: string;
};

/**
 * Which of the two things the user is doing — §4's distinction, in the two
 * phrasings a bookkeeper reads correctly.
 *
 * `ACQUISITION` is *buying something now*: it posts, so it needs a funding
 * account. `OPENING` is *recording something we already own*: its cost is
 * already in the ledger through the opening entry, so it posts nothing and a
 * funding account is refused.
 */
export type AcquisitionKind = 'ACQUISITION' | 'OPENING';

export type AssetFormModel = {
    isEdit: boolean;
    kind: AcquisitionKind;
    onKindChange: (kind: AcquisitionKind) => void;
    /** True on an opening asset — the form swaps funding for the opening position. */
    isOpening: boolean;
    openingAccumulated: string;
    onOpeningAccumulatedChange: (value: string) => void;
    /** ISO date; its month becomes `openingDepreciatedThrough`. */
    openingThroughDate: string;
    onOpeningThroughDateChange: (value: string) => void;
    nameKa: string;
    nameEn: string;
    categoryId: string | null;
    acquisitionDate: string;
    cost: string;
    salvageValue: string;
    usefulLifeMonths: string;
    fundingAccountId: string | null;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    onCategoryChange: (categoryId: string) => void;
    onAcquisitionDateChange: (value: string) => void;
    onCostChange: (value: string) => void;
    onSalvageChange: (value: string) => void;
    onUsefulLifeChange: (value: string) => void;
    onFundingAccountChange: (accountId: string | null) => void;
    categoryOptions: { id: string; label: string; defaultLifeMonths: number }[];
    accountOptions: AccountOption[];
    /** The selected category's mapping, shown read-only for confirmation. */
    mapping: MappedAccount[];
    submitted: boolean;
    /** i18n key per field, or null when the field is fine. */
    errors: Record<string, string | null>;
    isValid: boolean;
    isSaving: boolean;
    saveError: string | null;
    onSubmit: () => Promise<void>;
    /**
     * The acquisition draft awaiting review; null when nothing is open — which
     * is *always* the case for an opening asset, because it posts nothing.
     */
    draft: JournalEntry | null;
    onDraftClose: () => void;
    onDraftSaved: (entry: JournalEntry) => void;
};

const MAX_USEFUL_LIFE_MONTHS = 1200; // 100 years — a guard, not a policy.

/**
 * A category's default useful life, as the string the field holds.
 *
 * The default must be a **real prefilled value**, not the input's placeholder:
 * a greyed-out "60" in an empty field reads as filled in and then fails
 * validation on submit — which is exactly what it did the first time this form
 * was driven by hand.
 */
const defaultLifeFor = (
    categoryId: string | null,
    categories: AssetCategory[],
): string => {
    const category = categories.find(
        (candidate) => candidate.id === categoryId,
    );
    return category ? String(category.defaultUsefulLifeMonths) : '';
};

/**
 * The add / edit asset form.
 *
 * ## Two kinds of asset, and why the form insists on the difference
 *
 * **Buying something now** (`ACQUISITION`) creates the asset *and* a draft entry
 * — Dr the category's cost account, Cr the funding account — which is handed to
 * the Journal Entry form. Nothing posts here: `acquisitionEntryId` stays null
 * until a person posts that entry, and the server links it then.
 *
 * **Recording something we already own** (`OPENING`) creates the asset and
 * nothing else. A real tenant's existing assets already sit in the ledger
 * through the opening entry, so posting an acquisition for them would book their
 * cost a **second time** and break the tie-out on day one.
 *
 * That failure is invisible in testing — a seeded dev register reconciles
 * perfectly either way — which is why the choice is an explicit, unavoidable
 * field rather than an inferred default. The validation is strict in both
 * directions and the form mirrors it: the funding account is required on an
 * acquisition and refused on an opening asset.
 *
 * Editing never produces an entry: changing a name or a useful life is not a
 * transaction. Accumulated depreciation is not on this form at all — it is the
 * sum of posted charges, and an edit is not a posting.
 *
 * Amounts are validated with the project's `parseGelToTetri`, so sub-tetri input
 * is refused here exactly as the server refuses it.
 */
export const useAssetForm = ({
    asset,
    categories,
    accounts,
    locale,
    onClose,
    onSaved,
}: UseAssetFormParams): AssetFormModel => {
    const { t } = useTranslation('pages');
    const isEdit = asset !== null;

    const createMutation = useCreateFixedAssetMutation();
    const updateMutation = useUpdateFixedAssetMutation();
    const invalidatePosted = useInvalidatePostedAssetEntry();

    const [nameKa, setNameKa] = React.useState(asset?.name.ka ?? '');
    const [nameEn, setNameEn] = React.useState(asset?.name.en ?? '');
    const [categoryId, setCategoryId] = React.useState<string | null>(
        asset?.categoryId ?? categories[0]?.id ?? null,
    );
    const [acquisitionDate, setAcquisitionDate] = React.useState(
        asset?.acquisitionDate ?? '',
    );
    const [cost, setCost] = React.useState(asset?.cost ?? '');
    const [salvageValue, setSalvageValue] = React.useState(
        asset?.salvageValue ?? '',
    );
    const [usefulLifeMonths, setUsefulLifeMonths] = React.useState(() =>
        asset
            ? String(asset.usefulLifeMonths)
            : defaultLifeFor(categories[0]?.id ?? null, categories),
    );
    const [fundingAccountId, setFundingAccountId] = React.useState<
        string | null
    >(null);
    // Buying something now is the common case and the safe default: it posts,
    // and a posting that should not have happened is visible in the journal —
    // whereas a missing one shows up only as a tie-out difference later.
    const [kind, setKind] = React.useState<AcquisitionKind>('ACQUISITION');
    const [openingAccumulated, setOpeningAccumulated] = React.useState('');
    const [openingThroughDate, setOpeningThroughDate] = React.useState('');
    const [submitted, setSubmitted] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [draft, setDraft] = React.useState<JournalEntry | null>(null);

    /**
     * Categories can resolve after the drawer mounts (a cold cache). Adopt the
     * first one and its default life then, rather than leaving the form with no
     * category and an empty life the user has to notice and fix.
     */
    React.useEffect(() => {
        if (isEdit || categoryId !== null || categories.length === 0) {
            return;
        }
        const first = categories[0];
        if (!first) {
            return;
        }
        setCategoryId(first.id);
        setUsefulLifeMonths((current) =>
            current === '' ? String(first.defaultUsefulLifeMonths) : current,
        );
    }, [isEdit, categoryId, categories]);

    const categoryOptions = React.useMemo(
        () =>
            categories
                .filter((category) => !category.archived)
                .map((category) => ({
                    id: category.id,
                    label: resolveLabel(category.name, locale, category.id),
                    defaultLifeMonths: category.defaultUsefulLifeMonths,
                })),
        [categories, locale],
    );

    const accountOptions = React.useMemo<AccountOption[]>(
        () =>
            accounts
                .filter((account) => account.isPostable && !account.archived)
                .map((account) => {
                    const name = account.name;
                    return {
                        id: account.id,
                        code: account.code,
                        name,
                        searchText: `${account.code} ${name}`,
                    };
                })
                .sort((a, b) => a.code.localeCompare(b.code)),
        [accounts],
    );

    const selectedCategory = React.useMemo(
        () => categories.find((category) => category.id === categoryId) ?? null,
        [categories, categoryId],
    );

    /**
     * The account mapping, shown read-only. It is the category's, never the
     * asset's — surfacing it here is confirmation, not an editing affordance.
     */
    const mapping = React.useMemo<MappedAccount[]>(() => {
        if (!selectedCategory) {
            return [];
        }
        const byId = new Map(accounts.map((account) => [account.id, account]));
        const entries: { labelKey: string; accountId: string }[] = [
            {
                labelKey: 'fixedAssets.form.mapping.cost',
                accountId: selectedCategory.costAccountId,
            },
            {
                labelKey: 'fixedAssets.form.mapping.accumulated',
                accountId: selectedCategory.accumulatedDepreciationAccountId,
            },
            {
                labelKey: 'fixedAssets.form.mapping.expense',
                accountId: selectedCategory.expenseAccountId,
            },
        ];
        return entries.map((entry) => {
            const account = byId.get(entry.accountId);
            return {
                labelKey: entry.labelKey,
                code: account?.code ?? entry.accountId,
                name: account ? account.name : '',
            };
        });
    }, [selectedCategory, accounts]);

    // ── validation ──────────────────────────────────────────────────────────
    const costParse = parseGelToTetri(cost);
    const salvageParse = parseGelToTetri(salvageValue);
    const openingParse = parseGelToTetri(openingAccumulated);
    const salvageTetri = salvageParse.ok ? salvageParse.tetri : 0;
    const lifeNumber = Number(usefulLifeMonths.trim());
    const isOpening = !isEdit && kind === 'OPENING';

    const errors = React.useMemo<Record<string, string | null>>(() => {
        const amountErrorKey = (
            result: ReturnType<typeof parseGelToTetri>,
            requiredKey: string,
        ): string | null => {
            if (result.ok) {
                return null;
            }
            switch (result.reason) {
                case 'empty':
                    return requiredKey;
                case 'sub-tetri':
                    return 'fixedAssets.form.errors.subTetri';
                case 'negative':
                    return 'fixedAssets.form.errors.negative';
                default:
                    return 'fixedAssets.form.errors.invalidAmount';
            }
        };

        const costError =
            amountErrorKey(costParse, 'fixedAssets.form.errors.costRequired') ??
            (costParse.ok && costParse.tetri === 0
                ? 'fixedAssets.form.errors.costRequired'
                : null);

        // Salvage is optional — an empty field means "writes down to nil".
        const salvageError =
            salvageValue.trim() === ''
                ? null
                : (amountErrorKey(
                      salvageParse,
                      'fixedAssets.form.errors.invalidAmount',
                  ) ??
                  (salvageParse.ok &&
                  costParse.ok &&
                  salvageParse.tetri > costParse.tetri
                      ? 'fixedAssets.form.errors.salvageAboveCost'
                      : null));

        return {
            name:
                nameKa.trim() === '' && nameEn.trim() === ''
                    ? 'fixedAssets.form.errors.nameRequired'
                    : null,
            categoryId:
                categoryId === null
                    ? 'fixedAssets.form.errors.categoryRequired'
                    : null,
            acquisitionDate:
                acquisitionDate.trim() === ''
                    ? 'fixedAssets.form.errors.acquisitionDateRequired'
                    : null,
            cost: costError,
            salvageValue: salvageError,
            usefulLifeMonths:
                !Number.isInteger(lifeNumber) ||
                lifeNumber < 1 ||
                lifeNumber > MAX_USEFUL_LIFE_MONTHS
                    ? 'fixedAssets.form.errors.usefulLifeRequired'
                    : null,
            // Required on an acquisition, and *only* there — the server refuses
            // it on an opening asset (`FUNDING_ACCOUNT_NOT_ALLOWED`).
            fundingAccountId:
                !isEdit && !isOpening && fundingAccountId === null
                    ? 'fixedAssets.form.errors.fundingRequired'
                    : null,
            // The opening position: how much of this asset has already been
            // depreciated before the register existed. Optional (a brand-new
            // asset carried in has none) but never negative, and never more than
            // the asset can depreciate.
            openingAccumulated:
                !isOpening || openingAccumulated.trim() === ''
                    ? null
                    : (amountErrorKey(
                          openingParse,
                          'fixedAssets.form.errors.invalidAmount',
                      ) ??
                      (openingParse.ok &&
                      costParse.ok &&
                      openingParse.tetri > costParse.tetri - salvageTetri
                          ? 'fixedAssets.form.errors.openingAboveBase'
                          : null)),
        };
    }, [
        nameKa,
        nameEn,
        categoryId,
        acquisitionDate,
        // `cost` itself is not read here — `costParse` is derived from it and
        // changes with it.
        costParse,
        salvageValue,
        salvageParse,
        salvageTetri,
        lifeNumber,
        isEdit,
        isOpening,
        fundingAccountId,
        openingAccumulated,
        openingParse,
    ]);

    const isValid = Object.values(errors).every((error) => error === null);

    // ── behaviour ───────────────────────────────────────────────────────────

    /**
     * Switching category adopts its default life — but only while the field
     * still holds the previous category's default. A life the user typed is
     * theirs and survives the switch.
     */
    const onCategoryChange = React.useCallback(
        (nextId: string) => {
            const previousDefault = defaultLifeFor(categoryId, categories);
            setCategoryId(nextId);
            if (
                usefulLifeMonths.trim() === '' ||
                usefulLifeMonths === previousDefault
            ) {
                setUsefulLifeMonths(defaultLifeFor(nextId, categories));
            }
        },
        [categories, categoryId, usefulLifeMonths],
    );

    /** The fields both create and update share. */
    const sharedFields = React.useCallback(
        () => ({
            name: {
                ...(nameKa.trim() ? { ka: nameKa.trim() } : {}),
                ...(nameEn.trim() ? { en: nameEn.trim() } : {}),
            },
            categoryId: categoryId as string,
            acquisitionDate,
            cost: cost.trim(),
            salvageValue:
                salvageValue.trim() === '' ? '0' : salvageValue.trim(),
            usefulLifeMonths: lifeNumber,
            method: 'STRAIGHT_LINE' as const,
        }),
        [
            nameKa,
            nameEn,
            categoryId,
            acquisitionDate,
            cost,
            salvageValue,
            lifeNumber,
        ],
    );

    /**
     * The create payload — and the one place the two kinds of asset diverge.
     *
     * The opening fields travel only on an opening asset and `fundingAccountId`
     * only on an acquisition, because the server refuses each in the wrong
     * context. Sending a stray null would be a request that says something the
     * user did not.
     */
    const buildCreateRequest = React.useCallback(
        (id: string): CreateFixedAssetRequest => ({
            id,
            ...sharedFields(),
            isOpening,
            ...(isOpening
                ? {
                      openingAccumulatedDepreciation:
                          openingAccumulated.trim() === ''
                              ? '0'
                              : openingAccumulated.trim(),
                      ...(openingThroughDate
                          ? {
                                openingDepreciatedThrough:
                                    periodFromDate(openingThroughDate),
                            }
                          : {}),
                  }
                : { fundingAccountId }),
            locale,
        }),
        [
            sharedFields,
            isOpening,
            openingAccumulated,
            openingThroughDate,
            fundingAccountId,
            locale,
        ],
    );

    /** The update payload: no id, no funding account, no opening fields. */
    const buildUpdateRequest = React.useCallback(
        (): UpdateFixedAssetRequest => sharedFields(),
        [sharedFields],
    );

    const onSubmit = React.useCallback(async () => {
        setSubmitted(true);
        setSaveError(null);
        if (!isValid) {
            return;
        }

        if (isEdit && asset) {
            const result = await updateMutation.mutateAsync({
                assetId: asset.id,
                body: buildUpdateRequest(),
            });
            if (!result.success) {
                setSaveError(fixedAssetErrorMessage(result.error, t));
                return;
            }
            onClose();
            return;
        }

        // A client-generated UUID, so a retried create is idempotent — though
        // first-write-wins, so a retry with changed content keeps the original.
        const id = newEntryId();
        const result = await createMutation.mutateAsync(buildCreateRequest(id));
        if (!result.success) {
            setSaveError(fixedAssetErrorMessage(result.error, t));
            return;
        }

        // An opening asset posts nothing and `draft` is null — there is no entry
        // to review, so the drawer simply closes. That is the whole point of the
        // distinction, not a missing step.
        if (result.response.draft === null) {
            onSaved?.();
            onClose();
            return;
        }

        // The asset is in the register now; the entry is a draft until someone
        // posts it in the form about to open. Until then the tie-out reports the
        // difference — correctly, and visibly.
        setDraft(result.response.draft);
    }, [
        isValid,
        isEdit,
        asset,
        buildCreateRequest,
        buildUpdateRequest,
        updateMutation,
        createMutation,
        onClose,
        onSaved,
        t,
    ]);

    const onDraftClose = React.useCallback(() => {
        setDraft(null);
        // The asset exists either way — closing the draft unreviewed leaves it
        // in the register with no posted acquisition entry, which the tie-out
        // will report as a difference. That is correct, and visible.
        onClose();
    }, [onClose]);

    const onDraftSaved = React.useCallback(
        (entry: JournalEntry) => {
            // Nothing to commit — the server links a posted acquisition entry to
            // its asset itself. But posting is what puts the cost in the ledger,
            // so the register and the tie-out have to be refetched; the
            // journal's post mutation invalidates only the journal.
            invalidatePosted(entry);
            setDraft(null);
            onSaved?.();
            onClose();
        },
        [invalidatePosted, onClose, onSaved],
    );

    const onKindChange = React.useCallback((next: AcquisitionKind) => {
        setKind(next);
        // Each kind's exclusive fields are cleared on the way out, so a value
        // typed under one never travels under the other.
        setFundingAccountId(null);
        setOpeningAccumulated('');
        setOpeningThroughDate('');
    }, []);

    return {
        isEdit,
        kind,
        onKindChange,
        isOpening,
        openingAccumulated,
        onOpeningAccumulatedChange: setOpeningAccumulated,
        openingThroughDate,
        onOpeningThroughDateChange: setOpeningThroughDate,
        nameKa,
        nameEn,
        categoryId,
        acquisitionDate,
        cost,
        salvageValue,
        usefulLifeMonths,
        fundingAccountId,
        onNameKaChange: setNameKa,
        onNameEnChange: setNameEn,
        onCategoryChange,
        onAcquisitionDateChange: setAcquisitionDate,
        onCostChange: setCost,
        onSalvageChange: setSalvageValue,
        onUsefulLifeChange: setUsefulLifeMonths,
        onFundingAccountChange: setFundingAccountId,
        categoryOptions,
        accountOptions,
        mapping,
        submitted,
        errors,
        isValid,
        isSaving: createMutation.isPending || updateMutation.isPending,
        saveError,
        onSubmit,
        draft,
        onDraftClose,
        onDraftSaved,
    };
};
